import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, ArrowLeft, Video, Calendar, Clock, ExternalLink, Film } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminLiveSessions() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRecForm, setShowRecForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingRec, setEditingRec] = useState(null);
  const [tab, setTab] = useState("live");
  const [form, setForm] = useState({ title: "", description: "", scheduled_date: "", scheduled_time: "", meet_link: "", duration_minutes: 60, status: "upcoming" });
  const [recForm, setRecForm] = useState({ title: "", description: "", video_url: "", duration: "", recorded_date: "" });

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchData = async () => {
    const [courseRes, sessionsRes, recRes] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/courses/${courseId}/live-sessions`),
      api.get(`/courses/${courseId}/recorded-sessions`),
    ]);
    setCourse(courseRes.data);
    setSessions(sessionsRes.data);
    setRecordings(recRes.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/live-sessions/${editing}`, form);
    } else {
      await api.post(`/courses/${courseId}/live-sessions`, form);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ title: "", description: "", scheduled_date: "", scheduled_time: "", meet_link: "", duration_minutes: 60, status: "upcoming" });
    fetchData();
  };

  const handleRecSubmit = async (e) => {
    e.preventDefault();
    if (editingRec) {
      await api.put(`/recorded-sessions/${editingRec}`, recForm);
    } else {
      await api.post(`/courses/${courseId}/recorded-sessions`, recForm);
    }
    setShowRecForm(false);
    setEditingRec(null);
    setRecForm({ title: "", description: "", video_url: "", duration: "", recorded_date: "" });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this live session?")) {
      await api.delete(`/live-sessions/${id}`);
      fetchData();
    }
  };

  const handleDeleteRec = async (id) => {
    if (window.confirm("Delete this recording?")) {
      await api.delete(`/recorded-sessions/${id}`);
      fetchData();
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none admin-input";

  const getSessionStatus = (s) => {
    const now = new Date();
    const sessionTime = new Date(`${s.scheduled_date}T${s.scheduled_time}`);
    const endTime = new Date(sessionTime.getTime() + (s.duration_minutes || 60) * 60000);
    if (now >= sessionTime && now <= endTime) return "live";
    if (now > endTime) return "completed";
    return "upcoming";
  };

  return (
    <AdminLayout>
      <div data-testid="admin-live-sessions-page">
        <button onClick={() => navigate("/admin/courses")} className="flex items-center gap-1 text-sm text-[#0284C7] font-bold mb-3">
          <ArrowLeft size={16} /> Back to Courses
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Live Classes & Recordings</h1>
            <p className="text-xs text-slate-500">{course?.title}</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 mb-4 admin-card p-1 border rounded-lg" data-testid="session-tabs">
          <button onClick={() => setTab("live")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-md transition-none ${tab === "live" ? "bg-[#0284C7] text-white" : "admin-text-secondary"}`} data-testid="tab-live-sessions">
            <Video size={14} /> Schedule Live Class
          </button>
          <button onClick={() => setTab("recordings")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-md transition-none ${tab === "recordings" ? "bg-[#0284C7] text-white" : "admin-text-secondary"}`} data-testid="tab-recordings">
            <Film size={14} /> Recorded Sessions
          </button>
        </div>

        {/* LIVE SESSIONS TAB */}
        {tab === "live" && (
          <>
            <button data-testid="add-live-session-btn" onClick={() => { setShowForm(true); setEditing(null); setForm({ title: "", description: "", scheduled_date: "", scheduled_time: "", meet_link: "", duration_minutes: 60, status: "upcoming" }); }} className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 mb-4 rounded-lg">
              <Plus size={16} /> Schedule Live Class
            </button>

            {showForm && (
              <form onSubmit={handleSubmit} className="admin-card border rounded-lg p-4 mb-4 space-y-3" data-testid="live-session-form">
                <h3 className="text-sm font-bold admin-text mb-2">{editing ? "Edit Live Session" : "Schedule New Live Class"}</h3>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Title *</label>
                  <input data-testid="session-title" className={inputClass} placeholder="e.g., English Grammar Doubt Session" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                </div>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
                  <textarea data-testid="session-description" className={inputClass} rows={2} placeholder="What will be covered in this session?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Date *</label>
                    <input data-testid="session-date" type="date" className={inputClass} value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Time *</label>
                    <input data-testid="session-time" type="time" className={inputClass} value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Google Meet Link *</label>
                    <input data-testid="session-meet-link" className={inputClass} placeholder="https://meet.google.com/xxx-xxxx-xxx" value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Duration (mins)</label>
                    <input data-testid="session-duration" type="number" className={inputClass} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Status</label>
                  <select data-testid="session-status" className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live Now</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="submit" data-testid="save-session-btn" className="flex-1 bg-[#0284C7] text-white font-bold py-2.5 text-sm rounded-lg">{editing ? "Update" : "Schedule"}</button>
                  <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2.5 text-sm font-bold text-slate-500 border border-slate-300 rounded-lg">Cancel</button>
                </div>
              </form>
            )}

            {/* Sessions List */}
            {sessions.length > 0 ? (
              <div className="space-y-3">
                {sessions.map((s) => {
                  const status = getSessionStatus(s);
                  return (
                    <div key={s.id} className="admin-card border rounded-lg p-4" data-testid={`live-session-${s.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              status === "live" ? "bg-red-500 text-white" :
                              status === "completed" ? "bg-slate-400 text-white" :
                              s.status === "cancelled" ? "bg-orange-400 text-white" :
                              "bg-green-500 text-white"
                            }`}>{status === "live" ? "LIVE NOW" : status === "completed" ? "COMPLETED" : s.status === "cancelled" ? "CANCELLED" : "UPCOMING"}</span>
                          </div>
                          <h3 className="text-sm font-bold admin-text">{s.title}</h3>
                          {s.description && <p className="text-xs admin-text-secondary mt-0.5">{s.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs admin-text-secondary">
                            <span className="flex items-center gap-1"><Calendar size={12} /> {s.scheduled_date}</span>
                            <span className="flex items-center gap-1"><Clock size={12} /> {s.scheduled_time}</span>
                            <span>{s.duration_minutes} min</span>
                          </div>
                          {s.meet_link && (
                            <a href={s.meet_link.startsWith("http") ? s.meet_link : "https://" + s.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#0284C7] font-bold" data-testid={`meet-link-${s.id}`}>
                              <ExternalLink size={12} /> {s.meet_link}
                            </a>
                          )}
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button data-testid={`edit-session-${s.id}`} onClick={() => { setEditing(s.id); setForm({ title: s.title, description: s.description || "", scheduled_date: s.scheduled_date, scheduled_time: s.scheduled_time, meet_link: s.meet_link || "", duration_minutes: s.duration_minutes || 60, status: s.status || "upcoming" }); setShowForm(true); }} className="p-2 text-[#0284C7] bg-[#0284C7]/10 rounded">
                            <Edit size={14} />
                          </button>
                          <button data-testid={`delete-session-${s.id}`} onClick={() => handleDelete(s.id)} className="p-2 text-red-500 bg-red-500/10 rounded">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="admin-card border rounded-lg p-8 text-center">
                <Video size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm admin-text-secondary">No live sessions scheduled yet.</p>
                <p className="text-xs text-slate-400 mt-1">Click "Schedule Live Class" to create your first session.</p>
              </div>
            )}
          </>
        )}

        {/* RECORDINGS TAB */}
        {tab === "recordings" && (
          <>
            <button data-testid="add-recording-btn" onClick={() => { setShowRecForm(true); setEditingRec(null); setRecForm({ title: "", description: "", video_url: "", duration: "", recorded_date: "" }); }} className="w-full bg-amber-500 text-white font-bold py-3 text-sm flex items-center justify-center gap-2 mb-4 rounded-lg">
              <Plus size={16} /> Add Recording
            </button>

            {showRecForm && (
              <form onSubmit={handleRecSubmit} className="admin-card border rounded-lg p-4 mb-4 space-y-3" data-testid="recording-form">
                <h3 className="text-sm font-bold admin-text mb-2">{editingRec ? "Edit Recording" : "Add New Recording"}</h3>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Title *</label>
                  <input data-testid="rec-title" className={inputClass} placeholder="e.g., Grammar Session Recording" value={recForm.title} onChange={(e) => setRecForm({ ...recForm, title: e.target.value })} required />
                </div>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
                  <textarea data-testid="rec-description" className={inputClass} rows={2} placeholder="Brief description" value={recForm.description} onChange={(e) => setRecForm({ ...recForm, description: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Video URL * (Google Drive / YouTube / Direct MP4)</label>
                  <input data-testid="rec-video-url" className={inputClass} placeholder="https://drive.google.com/..." value={recForm.video_url} onChange={(e) => setRecForm({ ...recForm, video_url: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Duration</label>
                    <input data-testid="rec-duration" className={inputClass} placeholder="e.g., 45:00" value={recForm.duration} onChange={(e) => setRecForm({ ...recForm, duration: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Date Recorded</label>
                    <input data-testid="rec-date" type="date" className={inputClass} value={recForm.recorded_date} onChange={(e) => setRecForm({ ...recForm, recorded_date: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="submit" data-testid="save-recording-btn" className="flex-1 bg-amber-500 text-white font-bold py-2.5 text-sm rounded-lg">{editingRec ? "Update" : "Add Recording"}</button>
                  <button type="button" onClick={() => { setShowRecForm(false); setEditingRec(null); }} className="px-4 py-2.5 text-sm font-bold text-slate-500 border border-slate-300 rounded-lg">Cancel</button>
                </div>
              </form>
            )}

            {/* Recordings List */}
            {recordings.length > 0 ? (
              <div className="space-y-3">
                {recordings.map((r) => (
                  <div key={r.id} className="admin-card border rounded-lg p-4" data-testid={`recording-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold admin-text">{r.title}</h3>
                        {r.description && <p className="text-xs admin-text-secondary mt-0.5">{r.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs admin-text-secondary">
                          {r.duration && <span className="flex items-center gap-1"><Clock size={12} /> {r.duration}</span>}
                          {r.recorded_date && <span className="flex items-center gap-1"><Calendar size={12} /> {r.recorded_date}</span>}
                        </div>
                        {r.video_url && (
                          <a href={r.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-amber-600 font-bold">
                            <ExternalLink size={12} /> View Video
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button data-testid={`edit-rec-${r.id}`} onClick={() => { setEditingRec(r.id); setRecForm({ title: r.title, description: r.description || "", video_url: r.video_url || "", duration: r.duration || "", recorded_date: r.recorded_date || "" }); setShowRecForm(true); }} className="p-2 text-[#0284C7] bg-[#0284C7]/10 rounded">
                          <Edit size={14} />
                        </button>
                        <button data-testid={`delete-rec-${r.id}`} onClick={() => handleDeleteRec(r.id)} className="p-2 text-red-500 bg-red-500/10 rounded">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-card border rounded-lg p-8 text-center">
                <Film size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm admin-text-secondary">No recordings added yet.</p>
                <p className="text-xs text-slate-400 mt-1">Add recordings from your past live sessions.</p>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
